import { useEffect, useMemo, useState } from 'react';
import { differenceInUtcDays } from '@chainflip/utils/date';
import { utc } from '@date-fns/utc';
import { addSeconds, differenceInMonths, differenceInWeeks } from 'date-fns';
import { type Tab } from '../components/TabSelect';

export type ChartAggregationIntervalOption = Tab<'D' | 'W' | 'M'>;

export const useChartAggregation = (chartStartDate: Date, chartEndDate: Date) => {
  const chartAggregationIntervalOptions = useMemo(() => {
    /*
      chartEndDate will always be YYYY/MM/DD - 23:59:59
      In order to properly calculate the distance, we round the day up
    */
    const normalizedEndDate = addSeconds(chartEndDate, 1, { in: utc });

    const dateRange = { start: chartStartDate, end: normalizedEndDate };

    return [
      { label: 'D', disabled: differenceInUtcDays(dateRange) < 2, value: 'D' },
      {
        label: 'W',
        disabled: differenceInWeeks(normalizedEndDate, chartStartDate, { in: utc }) < 2,
        value: 'W',
      },
      {
        label: 'M',
        disabled: differenceInMonths(normalizedEndDate, chartStartDate, { in: utc }) < 2,
        value: 'M',
      },
    ] as ChartAggregationIntervalOption[];
  }, [chartStartDate, chartEndDate]);

  const [selectedChartAggregationInterval, setSelectedChartAggregationInterval] = useState(
    chartAggregationIntervalOptions[0],
  );

  useEffect(() => {
    if (
      chartAggregationIntervalOptions.some(
        (option) => option.value === selectedChartAggregationInterval.value && option.disabled,
      )
    )
      setSelectedChartAggregationInterval(chartAggregationIntervalOptions[0]);
  }, [chartAggregationIntervalOptions, chartStartDate, chartEndDate]);

  return {
    chartAggregationIntervalOptions,
    selectedChartAggregationInterval,
    setSelectedChartAggregationInterval,
  };
};
